import React, { useCallback, useEffect, useLayoutEffect, useState } from 'react'

import { useOverlayControl } from '@/components/ui/overlay/overlay-control.context'
import { PopupRequiredProps } from '@/factories'
import { MaybePromise } from '@/models'

import * as S from './popup-window.styled'

export type PopupWindowProps = PopupRequiredProps & {
  children: (props: {
    isInTransition: boolean
    isOpening: boolean
    handleClose: VoidFunction
  }) => React.JSX.Element
  lightBg?: boolean
  onBeforeClose?: () => MaybePromise<boolean>
}

export const PopupWindow = ({
  children,
  onClose,
  lightBg = false,
  onBeforeClose,
}: PopupWindowProps) => {
  // NOTE: When used inside popupFactory, isClosing/onAfterClose are injected via context.
  // isClosing signals the overlay to start its close animation.
  // onAfterClose is called once the animation completes (actual unmount trigger).
  const {
    isClosing: ctxIsClosing,
    onAfterClose,
    registerAnimated,
    isSuspended,
  } = useOverlayControl()

  // NOTE: By default, `PopupWindow` is rendered in the 'closed' state and is being in transition to 'opened' state
  const [isInTransition, setIsInTransition] = useState(true)
  // NOTE: isOpening controls the backdrop opacity.
  // When opening on top of a suspended modal, start at 1 immediately so the backdrop never flashes out.
  const [isOpening, setIsOpening] = useState(false)
  // NOTE: isContentOpening always starts false so the dialog box can animate in independently.
  // For stacked modals the backdrop is already solid, so only the content needs to transition.
  const [isContentOpening, setIsContentOpening] = useState(false)

  const handleClose = () => {
    const closePermission = onBeforeClose?.()

    if (closePermission instanceof Promise) {
      void closePermission.then((shouldClose) => {
        if (shouldClose === false) return

        setIsContentOpening(false)
        setIsOpening(false)
      })
      return
    }

    if (closePermission === false) return

    setIsContentOpening(false)
    setIsOpening(false)
  }

  const handleTransitionStart = useCallback((e: React.TransitionEvent<HTMLDivElement>) => {
    // NOTE: transitions bubbles up, so check that transitions are fired on the correct target
    if (e.target !== e.currentTarget) return

    setIsInTransition(true)
  }, [])

  const handleTransitionEnd = useCallback(
    (e: React.TransitionEvent<HTMLDivElement>) => {
      // NOTE: transitions bubbles up, so check that transitions are fired on the correct target
      if (e.target !== e.currentTarget) return

      setIsInTransition(false)
      // NOTE: onAfterClose is provided by popupFactory and handles actual unmounting after animation.
      // For direct (non-popupFactory) usage, fall back to onClose as before.
      if (!isContentOpening) (onAfterClose ?? onClose)()
    },
    [isContentOpening, onClose, onAfterClose],
  )

  useLayoutEffect(() => {
    registerAnimated?.()
  }, [])

  // NOTE: React to an external close signal from popupFactory (via context)
  useEffect(() => {
    if (!ctxIsClosing) return

    // Note: External close requests (e.g., popupFactory close) should bypass onBeforeClose.
    // onBeforeClose is intended only for user-triggered dismiss actions.
    // For stacked modals, skip the backdrop fade to avoid flashing the suspended overlay below.
    setIsContentOpening(false)
    setIsOpening(false)
  }, [ctxIsClosing])

  useEffect(() => {
    setIsOpening(true)
    setIsContentOpening(true)
  }, [])

  return (
    <S.Root $isOpening={isOpening} $isSuspended={isSuspended}>
      <S.Container
        $lightBg={lightBg}
        $isContentOpening={isContentOpening}
        onTransitionStart={handleTransitionStart}
        onTransitionEnd={handleTransitionEnd}
      >
        {children({ handleClose, isInTransition, isOpening })}
      </S.Container>
    </S.Root>
  )
}
